import { minimumRoles } from '../config/OnboardingConfig'
import { getFromIndexedDB, removeFromIndexedDB } from './idbUtils'

const isTokenExpired = async () => {
  const onboardingState = await getFromIndexedDB('onboardingState')

  if (!onboardingState) {
    console.error('No onboardingState found in storage.')
    return { accessTokenExpired: true, graphTokenExpired: true } // Default to expired
  }

  const { accessToken, graphToken } = onboardingState

  const checkExpiration = token => {
    try {
      const payloadBase64 = token.split('.')[1] // Extract payload part of JWT
      const decodedPayload = JSON.parse(atob(payloadBase64)) // Decode Base64
      const currentTime = Math.floor(Date.now() / 1000) // Current time in seconds

      return decodedPayload.exp < currentTime // Return true if expired
    } catch (error) {
      console.error('Failed to parse token:', error)
      return true // Consider token expired if parsing fails
    }
  }

  const accessTokenExpired = checkExpiration(accessToken)
  const graphTokenExpired = checkExpiration(graphToken)

  return {
    accessTokenExpired,
    graphTokenExpired,
  }
}

// Slim workflow: removed snackbar-based expiry hook

export const clearOnboardingIfTokenExpired = () => {
  if (isTokenExpired()) {
    console.log('Token has expired. Clearing local onboarding state...')
  } else {
    console.log('Token is still valid. Local onboarding state not cleared.')
  }
}

export const clearLocalOnboardingState = async () => {
  await removeFromIndexedDB('onboardingState')
  await removeFromIndexedDB('userProfile')
  await removeFromIndexedDB('firstLogin')
  window.location.reload()
}

export const calculateUnconsumedLicenses = licenses => {
  if (!licenses || licenses.length === 0) {
    return 0 // No licenses available
  }

  let totalConsumedUnits = 0
  let totalActiveUnits = 0

  licenses.forEach(license => {
    const licenseName = license.productSku.name
    if (
      licenseName.includes('Microsoft 365 Business') ||
      licenseName.includes('Microsoft 365 E') ||
      licenseName.includes('Microsoft 365 F')
    ) {
      // This is a primary license
      totalConsumedUnits += license.consumedUnits
      totalActiveUnits += license.activeUnits
    }
  })

  const difference = totalActiveUnits - totalConsumedUnits
  return difference < 0 ? 0 : difference
}

export function mergeGroupsIntoCustomers(customers, securityGroups) {
  return customers.map(customer => {
    // Build assignedGroups from the customers "assignments"
    const assignedGroups = (customer.assignments || [])
      .filter(
        assignment =>
          assignment.accessContainer.accessContainerType === 'securityGroup'
      )
      .map(assignment => {
        // Find matching security group data
        const groupId = assignment.accessContainer.accessContainerId
        const matchingGroup = securityGroups.find(g => g.details.id === groupId)

        // Fallback if none found
        const groupName = matchingGroup
          ? matchingGroup.details.displayName
          : 'Unknown Group'
        const groupMembers = matchingGroup ? matchingGroup.members : []

        // Collect roles from the assignment
        const rolesFromThisGroup =
          assignment.accessDetails?.unifiedRoles?.map(
            roleObj => roleObj.roleDefinitionId
          ) || []

        return {
          groupId,
          groupName,
          groupMembers,
          roles: rolesFromThisGroup,
        }
      })

    // Merged set of all roleDefinitionIds from assignedGroups
    const groupRolesSet = new Set()
    assignedGroups.forEach(grp => {
      grp.roles?.forEach(roleId => groupRolesSet.add(roleId))
    })

    // Compare to your minimumRoles
    const missingRoles = []
    minimumRoles.forEach(r => {
      if (!groupRolesSet.has(r.id)) {
        missingRoles.push(r.displayName)
      }
    })

    // Decide the status
    let groupStatus = 'No Group Roles'
    if (assignedGroups.length === 0) {
      groupStatus = 'No Group Roles'
    } else if (missingRoles.length === 0) {
      groupStatus = 'Inforcer Ready'
    } else {
      groupStatus = `Missing ${missingRoles.length} Roles`
    }

    // Compute users who can onboard (union of roles from all groups they belong to covers all minimum roles)
    const userIdToAggregate = new Map()
    assignedGroups.forEach(grp => {
      const rolesForGroup = new Set(grp.roles || [])
      ;(grp.groupMembers || []).forEach(member => {
        const memberId = member?.id || member?.objectId || ''
        if (!memberId) return
        const existing = userIdToAggregate.get(memberId) || {
          id: memberId,
          userPrincipalName: member?.userPrincipalName || member?.mail || '',
          mail: member?.mail || '',
          displayName: member?.displayName || '',
          roleIds: new Set(),
        }
        rolesForGroup.forEach(r => existing.roleIds.add(r))
        userIdToAggregate.set(memberId, existing)
      })
    })

    const requiredRoleIds = minimumRoles.map(r => r.id)
    const eligibleOnboarders = Array.from(userIdToAggregate.values())
      .filter(userAgg => requiredRoleIds.every(id => userAgg.roleIds.has(id)))
      .map(({ id, userPrincipalName, mail, displayName }) => ({ id, userPrincipalName, mail, displayName }))

    return {
      ...customer,
      assignedGroups,
      groupRoles: Array.from(groupRolesSet),
      groupMissingRoles: missingRoles,
      groupStatus,
      eligibleOnboarders,
    }
  })
}

export const getSecurityGroupStatus = customer => {
  // If no assignedGroups or empty => "No Group Relationship"
  if (!customer.assignedGroups || customer.assignedGroups.length === 0) {
    return {
      status: 'No Group Relationship',
      missingRoles: [],
    }
  }

  // Declare assignedGroups
  const assignedGroups = customer.assignedGroups || []

  // Gather all roleDefinitionIds from assignedGroups
  const groupRoles = new Set()
  assignedGroups.forEach(grp => {
    if (grp.roles) {
      grp.roles.forEach(r => groupRoles.add(r))
    }
  })

  // Compare groupRoles to minimumRoles
  const missing = []
  minimumRoles.forEach(({ id, displayName }) => {
    if (!groupRoles.has(id)) {
      missing.push(displayName)
    }
  })

  if (missing.length === 0) {
    return {
      status: 'Group Ready',
      missingRoles: [],
    }
  } else {
    return {
      status: `Missing ${missing.length} Roles`,
      missingRoles: missing,
    }
  }
}


